import axios from "@/utils/axios";
import { useToast } from "@elaniintc/goes-design-system";
import type {
  ExternalData,
  ExternalDataFunction,
  ExternalDataType,
} from "@sdt/render-form-engine";
import type { AxiosRequestConfig } from "axios";
import type { QueryClient } from "@tanstack/react-query";
import type { ValidationError } from "./error-handling";
import {
  fetchExpedientData,
  fetchStandardData,
  getExpedientFieldId,
  isExpedientTable,
  mapExpedientItemToOption,
  parseExpedientItems,
} from "./external-data.helpers";
import type {
  ExpedientDateFieldMap,
  ResponseMeta,
} from "./external-data.helpers";

export const createDataCenterFetcher = (
  queryClient: QueryClient,
  dateFields?: ExpedientDateFieldMap,
): ExternalDataFunction => {
  return async (
    table_name: string,
    source: string | undefined | null = "SDT",
    filters:
      | Array<{
          name: string;
          operator?: string;
          type: string;
          value: string | number | boolean | null;
        }>
      | undefined
      | null = [],
    queryFilters: {
      search?: string;
      page?: number;
    } = {},
    column: string | null = null,
  ) => {
    try {
      const normalizedFiltersParams = filters ?? [];
      const normalizedQueryFiltersParams = {
        ...(queryFilters.search && { search: queryFilters.search }),
        page: queryFilters.page || 1,
      };

      const { page, ...normalizedQueryFilters } = normalizedQueryFiltersParams;

      const data = await queryClient.fetchQuery({
        queryKey: [
          "data-center-table-data",
          table_name,
          source,
          normalizedFiltersParams,
          normalizedQueryFiltersParams,
          column,
        ],
        queryFn: async (): Promise<ExternalData> => {
          if (isExpedientTable(table_name, source)) {
            return fetchExpedientData(
              table_name,
              normalizedQueryFilters.search ?? "",
              page,
              dateFields,
            );
          }
          return fetchStandardData(
            table_name,
            source,
            normalizedFiltersParams,
            queryFilters.search,
            queryFilters.page || 1,
            column,
          );
        },
        staleTime: isExpedientTable(table_name, source) ? 0 : 5 * 60 * 1000,
      });
      return data;
    } catch (error) {
      console.error(`Error fetching data from table ${table_name}:`, error);
      const errResponse = (
        error as { response?: { data?: { meta?: unknown } } }
      )?.response;
      if (errResponse?.data?.meta) {
        const emptyDataWithMeta: ExternalData & { meta?: ResponseMeta } = {
          data: [],
          meta: errResponse.data.meta as ResponseMeta,
        };
        return emptyDataWithMeta;
      }
      return { data: [] };
    }
  };
};

export interface Country {
  country_code: string;
  name: string;
  calling_code: string;
  flag_url: string;
}

export const createCountryDataFetcher = (queryClient: QueryClient) => {
  return async (): Promise<Country[]> => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["countries-data"],
        queryFn: async (): Promise<Country[]> => {
          const response = await axios.get(
            "/v1/config/phone-countries?locale=es",
          );
          return response.data.countries || [];
        },
        staleTime: 0,
      });
      return data;
    } catch (error) {
      console.error("Error fetching countries:", error);
      return []; // ← returns an empty array instead of { data: [] }
    }
  };
};

type ParamValue = string | number | boolean | null | undefined;
const NUMBER_FIELDS = new Set(["registry_number", "company_registry_number"]);
const STRING_FIELDS = new Set(["legal_rep_dui", "owner_dui"]);
const coerceFieldValue = (name: string, value: unknown): ParamValue => {
  if (NUMBER_FIELDS.has(name)) return Number(value);
  if (STRING_FIELDS.has(name))
    return (value as ParamValue)?.toString() ?? (value as ParamValue);
  return value as ParamValue;
};

export const createInteroperabilityFetcher = (queryClient: QueryClient) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const toast = useToast();
  let lastErrorToastAt = 0;
  const ERROR_TOAST_COOLDOWN_MS = 4000;

  return async (
    config: Record<string, unknown>,
    inputData: Record<string, unknown> | string | number | boolean | null,
  ): Promise<Record<string, unknown>> => {
    try {
      const cfg = config as {
        service?: string;
        action?: string;
        input_field?: Array<{ name: string }>;
        params?: Record<string, ParamValue>;
      };
      const { service, action } = cfg || {};

      if (!service || !action) {
        console.warn("Service or action missing in interoperability config");
        return {};
      }

      const data = await queryClient.fetchQuery({
        queryKey: [
          "interoperability-query",
          service,
          action,
          cfg.params,
          inputData,
        ],
        retry: 0,
        queryFn: async () => {
          const paramsObject: Record<string, ParamValue> = {};
          const isInputObject =
            typeof inputData === "object" && inputData !== null;
          const fields: { name: string }[] = Array.isArray(cfg.input_field)
            ? cfg.input_field
            : [];

          for (const { name } of fields) {
            const raw =
              isInputObject && name in inputData ? inputData[name] : inputData;
            paramsObject[name] = coerceFieldValue(name, raw);
          }

          const payload = {
            service,
            action,
            ...(Object.keys(paramsObject).length > 0 && {
              params: paramsObject,
            }),
          };

          const requestConfig: AxiosRequestConfig & {
            metadata?: {
              skip401Redirect?: boolean;
              skip403Redirect?: boolean;
            };
          } = {
            metadata: {
              skip401Redirect: true,
              skip403Redirect: true,
            },
          };

          const response = await axios.post(
            "/admin/v1/interoperability/query",
            payload,
            requestConfig,
          );
          return response.data || null;
        },
        staleTime: 0,
      });

      return {
        data,
        config,
      };
    } catch (err) {
      const error = err as ValidationError;
      console.error(`Error fetching interoperability data:`, error);
      let description =
        "Ocurrió un error al intentar obtener los datos de interoperabilidad.";

      if (error?.response?.status === 400) {
        const errs = error.response.data;
        console.log("errs:", errs);
        if (
          errs?.message?.includes(
            "Los parámetros proporcionados no son válidos para la acción especificada.",
          )
        ) {
          description =
            "Los datos proporcionados no son válidos. Verifica la información e intenta nuevamente.";
        }
      } else if (error?.response?.status === 404) {
        description = "No existen datos para la consulta realizada.";
      }

      // avoid multiple toasts in a short period of time
      const now = Date.now();
      if (now - lastErrorToastAt > ERROR_TOAST_COOLDOWN_MS) {
        lastErrorToastAt = now;
        toast.showToast({
          title: "Error de interoperabilidad",
          description,
          options: { type: "error", duration: 5000 },
        });
      }

      return {};
    }
  };
};

export const createDataByIdFetcher = (queryClient: QueryClient) => {
  return async (table: string, source: string, id: string) => {
    try {
      if (isExpedientTable(table, source)) {
        const fieldId = getExpedientFieldId(table);

        const response = await axios.get(
          `/admin/v1/forms/expedient-fields/${fieldId}/search`,
          {
            params: {
              search: id,
              per_page: 1,
            },
          },
        );

        const items = parseExpedientItems(response.data);

        if (items.length > 0) {
          return mapExpedientItemToOption(items[0]);
        }

        return {
          id,
          value: id,
          label: id,
        };
      }

      const data = await queryClient.fetchQuery({
        queryKey: ["data-center", "table-data-by-id", table, id],
        queryFn: async (): Promise<
          | {
              id: string | number;
              [key: string]: ExternalDataType;
            }
          | undefined
        > => {
          const response = await axios.get(
            "/admin/v1/data-center/tables/data",
            {
              params: {
                table_name: table,
                source: source,
                "filters[0][name]": "id",
                "filters[0][operator]": "EQUALS",
                "filters[0][value]": id,
                "filters[0][type]": "STRING",
              },
            },
          );
          return response.data;
        },
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      });

      return data;
    } catch (error) {
      console.error(
        `Error fetching data from table ${table} by id ${id}:`,
        error,
      );
      return undefined;
    }
  };
};
